import { ArrowUpRight, MapPin, MoreHorizontal, Star } from "lucide-react";
import { Badge, Button, Card, CardHeader } from "@vseh/ui";
import {
  AgentBubble,
  AgentStatus,
  ApprovalGate,
  ConfidenceBadge,
  CostMeter,
  StreamingText,
  ToolCallCard,
  UserBubble,
} from "@vseh/ai-components";

import { useT } from "../shared/i18n";

export function Dashboard(): JSX.Element {
  const t = useT();
  return (
    <div className="flex flex-col gap-6">
      <header className="flex items-end justify-between">
        <div>
          <h1 className="font-display text-2xl font-semibold">{t("dashboard.title")}</h1>
          <p className="text-sm text-base-600">{t("dashboard.header.date")}</p>
        </div>
        <CostMeter
          rubPerUsd={92}
          entries={[
            { label: "gpt-4o",      calls: 6, inputTokens: 1240, outputTokens: 844, costUsd: 0.018 },
            { label: "gpt-4o-mini", calls: 3, inputTokens: 540,  outputTokens: 320, costUsd: 0.004 },
            { label: "ollama:qwen2.5:14b", calls: 12, inputTokens: 2400, outputTokens: 1100, costUsd: 0 },
          ]}
        />
      </header>

      {/* ---- KPI row ---- */}
      <div className="grid grid-cols-4 gap-4">
        <KpiCard label={t("dashboard.kpi.revenue")}   value="187 400 ₽" trend={t("dashboard.kpi.trend.revenue")}   tone="ai" />
        <KpiCard label={t("dashboard.kpi.published")} value="6"         trend={t("dashboard.kpi.trend.published")} tone="success" />
        <KpiCard label={t("dashboard.kpi.active")}    value="3"         trend={t("dashboard.kpi.trend.active")}    tone="neutral" />
        <KpiCard label={t("dashboard.kpi.ai_cost")}   value="$0.42"     trend={t("dashboard.kpi.trend.ai_cost")}   tone="ai" />
      </div>

      {/* ---- Two-column body ---- */}
      <div className="grid grid-cols-3 gap-4">
        <ActivityFeed className="col-span-2" />
        <FleetMapStub />
      </div>

      {/* ---- AI conversation example ---- */}
      <Card>
        <CardHeader
          title={t("dashboard.session.title")}
          description={t("dashboard.session.subtitle")}
        />
        <div className="space-y-4">
          <UserBubble timestamp="14:21">
            {t("dashboard.demo.q1")}
          </UserBubble>

          <AgentBubble
            agentLabel="AI Dispatcher"
            meta={{ model: "gpt-4o", latencyMs: 8200, costUsd: 0.018 }}
            tools={[
              <ToolCallCard
                key="search"
                name="search.equipment"
                arguments={{ category: "30t-excavator", region: t("dashboard.demo.region"), duration_weeks: 2 }}
                resultSummary="→ 14 results"
                latencyMs={1240}
              />,
              <ToolCallCard
                key="rank"
                name="ranking.score"
                arguments={{ ids: ["L-1241", "L-2030", "L-9001"], dims: ["rating", "distance", "availability"] }}
                resultSummary="→ ranked 3 / 14"
                latencyMs={420}
              />,
            ]}
          >
            <AgentStatus status="completed" label={t("dashboard.demo.summary")} />
            <ul className="mt-3 space-y-2 text-sm">
              <li className="flex items-start gap-3">
                <span className="font-mono tabular-nums text-base-500">1.</span>
                <div className="flex-1">
                  <div className="font-medium text-base-900">Hyundai R300LC-9SH</div>
                  <div className="text-base-600">
                    {t("dashboard.demo.l1.spec")}
                  </div>
                </div>
                <Badge tone="ai" dot>{t("dashboard.demo.l1.badge")}</Badge>
              </li>
              <li className="flex items-start gap-3">
                <span className="font-mono tabular-nums text-base-500">2.</span>
                <div className="flex-1">
                  <div className="font-medium text-base-900">Komatsu PC300-8</div>
                  <div className="text-base-600">{t("dashboard.demo.l2.spec")}</div>
                </div>
                <Badge tone="success" dot>{t("dashboard.demo.l2.badge")}</Badge>
              </li>
              <li className="flex items-start gap-3">
                <span className="font-mono tabular-nums text-base-500">3.</span>
                <div className="flex-1">
                  <div className="font-medium text-base-900">Cat 330D</div>
                  <div className="text-base-600">{t("dashboard.demo.l3.spec")}</div>
                </div>
                <Badge tone="heritage" dot>{t("dashboard.demo.l3.badge")}</Badge>
              </li>
            </ul>
            <div className="mt-3 flex items-center gap-2">
              <Button variant="ai" size="sm">{t("dashboard.demo.book")}</Button>
              <Button variant="secondary" size="sm">{t("dashboard.demo.compare")}</Button>
              <Button variant="ghost" size="sm">{t("dashboard.demo.more")}</Button>
            </div>
          </AgentBubble>

          <UserBubble timestamp="14:23">
            {t("dashboard.demo.q2")}
          </UserBubble>

          <AgentBubble
            agentLabel="AI Dispatcher"
            meta={{ model: "gpt-4o", latencyMs: 2400, costUsd: 0.011 }}
            tools={[
              <ToolCallCard
                key="prep"
                name="deal.prepare"
                arguments={{ listing_id: "L-1241", from: "2026-05-12", to: "2026-05-26" }}
                resultSummary="→ deal draft #SD-9183"
                latencyMs={620}
              />,
            ]}
          >
            <StreamingText
              done
              text={t("dashboard.demo.confirm")}
            />
            <div className="mt-3">
              <ApprovalGate
                title={t("dashboard.gate.title")}
                details={
                  <dl className="grid grid-cols-[max-content_1fr] gap-x-4 gap-y-1 text-sm">
                    <dt className="text-base-500">{t("dashboard.gate.kv.deal")}</dt>
                    <dd className="font-mono">#SD-9183</dd>
                    <dt className="text-base-500">{t("dashboard.gate.kv.supplier")}</dt>
                    <dd>{t("dashboard.gate.kv.supplier.v")}</dd>
                    <dt className="text-base-500">{t("dashboard.gate.kv.period")}</dt>
                    <dd>{t("dashboard.gate.kv.period.v")}</dd>
                    <dt className="text-base-500">{t("dashboard.gate.kv.amount")}</dt>
                    <dd className="font-mono tabular-nums">{t("dashboard.gate.kv.amount.v")}</dd>
                    <dt className="text-base-500">{t("dashboard.gate.kv.deposit")}</dt>
                    <dd className="font-mono tabular-nums">{t("dashboard.gate.kv.deposit.v")}</dd>
                  </dl>
                }
                risk={t("dashboard.gate.risk")}
                primaryLabel={t("dashboard.gate.primary")}
                secondaryLabel={t("dashboard.gate.secondary")}
              />
            </div>
            <div className="mt-2 flex items-center gap-2 text-2xs text-base-500">
              <ConfidenceBadge value={92} source={t("dashboard.gate.confidence_source")} />
            </div>
          </AgentBubble>
        </div>
      </Card>
    </div>
  );
}

// =============================================================================

interface KpiCardProps {
  label: string;
  value: string;
  trend: string;
  tone: "ai" | "success" | "neutral" | "heritage";
}

function KpiCard({ label, value, trend, tone }: KpiCardProps): JSX.Element {
  const trendColor = {
    ai: "text-ai-700",
    success: "text-success-700",
    heritage: "text-heritage-700",
    neutral: "text-base-500",
  }[tone];
  return (
    <Card density="comfortable">
      <div className="text-xs text-base-500">{label}</div>
      <div className="mt-1 font-display text-3xl font-semibold tabular-nums text-base-900">
        {value}
      </div>
      <div className={`mt-1 text-xs ${trendColor}`}>{trend}</div>
    </Card>
  );
}

function ActivityFeed({ className }: { className?: string }): JSX.Element {
  const t = useT();
  return (
    <Card density="comfortable" className={className}>
      <CardHeader
        title={t("dashboard.activity.title")}
        actions={<Button variant="ghost" size="xs" rightIcon={<ArrowUpRight className="size-3" />}>{t("dashboard.activity.all")}</Button>}
      />
      <ul className="space-y-3 text-sm">
        <FeedItem
          time="14:23"
          status="completed"
          text={t("dashboard.activity.item1")}
        />
        <FeedItem
          time="14:08"
          status="completed"
          text={t("dashboard.activity.item2")}
        />
        <FeedItem
          time="13:45"
          status="waiting_human"
          text={t("dashboard.activity.item3")}
          actions={
            <span className="flex items-center gap-2">
              <Button variant="primary" size="xs">{t("dashboard.activity.item3.approve")}</Button>
              <Button variant="ghost" size="xs">{t("dashboard.activity.item3.reject")}</Button>
            </span>
          }
        />
        <FeedItem
          time="13:12"
          status="acting"
          text={t("dashboard.activity.item4")}
        />
      </ul>
    </Card>
  );
}

interface FeedItemProps {
  time: string;
  status: "completed" | "waiting_human" | "acting" | "failed";
  text: string;
  actions?: React.ReactNode;
}

function FeedItem({ time, status, text, actions }: FeedItemProps): JSX.Element {
  return (
    <li className="flex items-start gap-3">
      <span className="mt-1 w-12 shrink-0 font-mono text-2xs tabular-nums text-base-500">{time}</span>
      <div className="min-w-0 flex-1">
        <AgentStatus status={status} label={text} />
        {actions && <div className="mt-1.5">{actions}</div>}
      </div>
    </li>
  );
}

function FleetMapStub(): JSX.Element {
  const t = useT();
  return (
    <Card density="comfortable">
      <CardHeader
        title={t("dashboard.map.title")}
        actions={<Button variant="ghost" size="xs" rightIcon={<ArrowUpRight className="size-3" />}>{t("dashboard.map.open")}</Button>}
      />
      <div className="relative grid h-56 place-items-center rounded-md border border-dashed border-base-300 bg-base-50">
        <div className="flex flex-col items-center gap-2 text-sm text-base-500">
          <MapPin className="size-8" />
          {t("dashboard.map.placeholder")}
        </div>
      </div>
      <div className="mt-3 flex items-center justify-between text-2xs">
        <Badge tone="success" dot>{t("dashboard.map.available")}</Badge>
        <Badge tone="heritage" dot>{t("dashboard.map.on_deal")}</Badge>
        <Badge tone="neutral" dot>Offline: 1</Badge>
      </div>
    </Card>
  );
}

// Iconic placeholder, removed `Star` warning
void Star;
void MoreHorizontal;
